// Beat 8 visual: the "arcade pricing" composition.
//
// A stylized arcade cabinet (top-half silhouette) rises from below the
// viewport and parks at the bottom of the screen. While it sits there, a
// Loon coin drops from the top of the viewport, falls into the cabinet's
// coin slot, and as it lands the on-screen game artwork swaps to the next
// title. Three coin-drop cycles fire across the beat — each coin's drop
// overlaps the previous coin's "landed" hold so the rhythm feels like a
// real arcade session (drop, play, drop, play).
//
// The cabinet itself is drawn with positioned divs (rounded purple shell,
// dark CRT screen, marquee strip, coin slot). Only the screen content +
// the falling coin are animated; the cabinet enters once and stays put.
// prefers-reduced-motion: cabinet is static and the screen shows a single
// game cover, no coin drops.

import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';
import { Link } from 'react-router';

import { ASSETS } from '@airhead-games/shared/configs';
import { getColorStop } from '../../../styles/playerColors';
import {
  easeOutCubic,
  mapProgress,
} from '../../../utilities/scrollTimelineMath';

type BeatEightArcadePricingProps = {
  scrollVh: MotionValue<number>;
  /** Global vh where the host segment starts. */
  segmentStartVh: number;
  /** Host segment duration in vh. */
  segmentDurationVh: number;
};

type ScreenCover = {
  src: string;
  /** Display name shown on the marquee strip above the screen. */
  name: string;
  /** Game-discovery slug — drives `/discover/clips?game=<slug>` on the badge. */
  slug: string;
};

// Game covers used as placeholders for the on-screen artwork. These will
// be swapped for real in-game screenshots once they're added to ASSETS.
// Three distinct covers — one per coin-drop cycle — so the rotation reads
// clearly as you scroll. `name` powers the marquee, `slug` powers the
// per-cover "Watch clips" link.
const SCREEN_COVERS: ScreenCover[] = [
  {
    src: ASSETS.coverTooManyCooks,
    name: 'Too Many Cooks',
    slug: 'too-many-cooks',
  },
  {
    src: ASSETS.coverWTFIsHappening,
    name: 'WTF Is Happening',
    slug: 'wtf-is-happening',
  },
  { src: ASSETS.coverMemeMash, name: 'Meme Mash', slug: 'meme-mash' },
];

// ───────────────────────── CHOREOGRAPHY ─────────────────────────
// All percentages are of the host segment's vh range.

/** Cabinet entrance: slides up from below the viewport and fades in. */
const CABINET_ENTER_START_PCT = 0;
const CABINET_ENTER_END_PCT = 14;

/** Cabinet exit: slides back down and fades out, synced to the hero panel. */
const CABINET_EXIT_START_PCT = 82;
const CABINET_EXIT_END_PCT = 100;

/** Px the cabinet travels on entrance/exit (positive = below viewport). */
const CABINET_OFFSCREEN_PX = 520;

type CoinCycle = {
  /** Scroll % at which the coin starts falling from above the viewport. */
  fallStartPct: number;
  /** Scroll % at which the coin reaches the cabinet's slot. */
  landPct: number;
  /** Scroll % at which the coin has fully faded after entering the slot. */
  hideEndPct: number;
  /** Index into SCREEN_COVERS that becomes visible at landPct. */
  coverIndex: number;
};

/** Three coin-drop cycles. The next coin starts falling shortly after the
 *  previous one lands, so a fresh coin is always in motion above the
 *  cabinet while the previous screen cover is still showing. */
const COIN_CYCLES: CoinCycle[] = [
  { fallStartPct: 14, landPct: 28, hideEndPct: 32, coverIndex: 0 },
  { fallStartPct: 32, landPct: 48, hideEndPct: 52, coverIndex: 1 },
  { fallStartPct: 52, landPct: 70, hideEndPct: 74, coverIndex: 2 },
];

/** Px the coin travels from above the viewport to the cabinet's coin slot.
 *  Sized so the coin clearly enters from offscreen on any reasonable
 *  viewport height; combined with `top: -10vh` the coin starts ~10vh above
 *  the visible region. */
const COIN_FALL_DISTANCE_PX_FRAC_OF_VH = 0.95;
const COIN_FALL_DISTANCE_MIN_PX = 600;

/** Cross-fade window for screen-cover swaps, in vh of scroll. Wide enough
 *  that the swap reads as a real transition (not a flicker) but tight
 *  enough that each cover spends most of its window fully visible. */
const SCREEN_FADE_VH = 4;

const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

const useViewportHeight = (): number => {
  const [h, setH] = React.useState(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setH(window.innerHeight);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return h;
};

const BeatEightArcadePricing: React.FC<BeatEightArcadePricingProps> = ({
  scrollVh,
  segmentStartVh,
  segmentDurationVh,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const viewportH = useViewportHeight();

  const pctToVh = React.useCallback(
    (pct: number) => segmentStartVh + (pct / 100) * segmentDurationVh,
    [segmentStartVh, segmentDurationVh]
  );

  const cabinetEnterStartVh = pctToVh(CABINET_ENTER_START_PCT);
  const cabinetEnterEndVh = pctToVh(CABINET_ENTER_END_PCT);
  const cabinetExitStartVh = pctToVh(CABINET_EXIT_START_PCT);
  const cabinetExitEndVh = pctToVh(CABINET_EXIT_END_PCT);

  const cabinetOpacity = useTransform(scrollVh, (v) => {
    const entered = easeOutCubic(
      mapProgress(v, cabinetEnterStartVh, cabinetEnterEndVh)
    );
    const exited = easeOutCubic(
      mapProgress(v, cabinetExitStartVh, cabinetExitEndVh)
    );
    return entered * (1 - exited);
  });

  const cabinetY = useTransform(scrollVh, (v) => {
    const entered = easeOutCubic(
      mapProgress(v, cabinetEnterStartVh, cabinetEnterEndVh)
    );
    const exited = easeOutCubic(
      mapProgress(v, cabinetExitStartVh, cabinetExitEndVh)
    );
    return (1 - entered) * CABINET_OFFSCREEN_PX + exited * CABINET_OFFSCREEN_PX;
  });

  return (
    <motion.div className="absolute inset-0 overflow-hidden">
      {/* Falling coins — drawn behind the cabinet shell so the coin
       *  visually disappears into the slot at the landing moment. */}
      {!reducedMotion &&
        COIN_CYCLES.map((cycle, index) => (
          <FallingCoin
            key={`coin-${index}`}
            cycle={cycle}
            scrollVh={scrollVh}
            pctToVh={pctToVh}
            viewportH={viewportH}
          />
        ))}

      {/* Cabinet — anchored to the bottom of the viewport. */}
      <motion.div
        className="absolute inset-x-0 bottom-0 flex justify-center px-4 sm:px-6 md:px-8"
        style={{ opacity: cabinetOpacity, y: cabinetY }}
      >
        <Cabinet
          covers={SCREEN_COVERS}
          coinCycles={COIN_CYCLES}
          scrollVh={scrollVh}
          pctToVh={pctToVh}
          reducedMotion={reducedMotion}
        />
      </motion.div>
    </motion.div>
  );
};

// ─────────────────────────── CABINET ────────────────────────────

type CabinetProps = {
  covers: ScreenCover[];
  coinCycles: CoinCycle[];
  scrollVh: MotionValue<number>;
  pctToVh: (pct: number) => number;
  reducedMotion: boolean;
};

const Cabinet: React.FC<CabinetProps> = ({
  covers,
  coinCycles,
  scrollVh,
  pctToVh,
  reducedMotion,
}) => {
  const shellColor = getColorStop('purple', '700');
  const shellEdgeColor = getColorStop('purple', '900');
  const slotColor = getColorStop('purple', '950');
  const marqueeColor = getColorStop('purple', '800');
  const screenColor = getColorStop('purple', '950');

  // Each cover's visible window: shows when its assigned coin lands, hides
  // when the next cover's coin lands. The final cover stays until the
  // segment ends (Infinity sentinel = no further swap-out).
  const showHideVhs = covers.map((_, idx) => {
    const cycle = coinCycles.find((c) => c.coverIndex === idx);
    return cycle ? pctToVh(cycle.landPct) : pctToVh(0);
  });

  return (
    <div
      className={[
        // Width ladder: cabinet stays compact on phones, grows up to QHD/4K.
        'relative w-full',
        'max-w-[22rem]',
        'sm:max-w-md',
        'md:max-w-lg',
        'lg:max-w-xl',
        'xl:max-w-2xl',
        '2xl:max-w-3xl',
        'min-[1920px]:max-w-4xl',
        'min-[2560px]:max-w-5xl',
        // Short-viewport overrides — the cabinet's height tracks its width
        // (screen aspect 16/10 + marquee + padding), so on 1280×720 and
        // 1366×768-class desktops the standard xl/2xl cap (672/768px wide)
        // makes the cabinet eat the whole viewport. Cap it harder when the
        // viewport is short, using mutually-exclusive height × width ranges
        // so Tailwind v4's media-query sort order doesn't matter. Mobile/
        // tablet short ranges already stay compact via the base ladder.
        '[@media(max-height:720px)_and_(min-width:1024px)]:max-w-md',
        '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:max-w-lg',
      ].join(' ')}
    >
      {/* Cabinet shell — purple rounded body. Rounded-only at the top so it
       *  reads as bolted to the floor of the viewport. */}
      <div
        className={[
          'relative rounded-t-[2rem] px-3 pt-3 pb-4',
          'sm:px-4 sm:pt-4 sm:pb-5',
          'md:px-5 md:pt-5 md:pb-6',
        ].join(' ')}
        style={{ backgroundColor: shellColor }}
      >
        {/* Coin slot — a dark purple slit on the top edge of the cabinet
         *  shell. Coins drop into it. */}
        <div
          className={[
            'absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
            'h-2.5 w-20 rounded-full',
            'sm:h-3 sm:w-24',
            'md:h-3.5 md:w-28',
          ].join(' ')}
          style={{ backgroundColor: slotColor }}
        />

        {/* Marquee band — a darker-purple strip hosting the active game's
         *  name. Stacked labels cross-fade in sync with the screen-cover
         *  swap. The invisible sizer span keeps the band one line tall
         *  regardless of which label is active. */}
        <div
          className="relative rounded-md py-1.5 sm:py-2"
          style={{
            backgroundColor: marqueeColor,
            fontSize: 'clamp(0.85rem, 2.6vw, 1.5rem)',
            lineHeight: 1.5,
          }}
        >
          <span aria-hidden className="invisible block px-3 text-center">
            G
          </span>
          {covers.map((cover, index) => (
            <MarqueeLabel
              key={`marquee-${cover.slug}`}
              cover={cover}
              index={index}
              showAtVh={showHideVhs[index] ?? pctToVh(0)}
              hideAtVh={showHideVhs[index + 1] ?? Number.POSITIVE_INFINITY}
              scrollVh={scrollVh}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>

        {/* Screen — dark purple rectangle that holds the rotating game
         *  artwork. Aspect kept landscape so covers fill the frame nicely. */}
        <div
          className={[
            'relative mt-3 overflow-hidden rounded-lg',
            'aspect-[16/10]',
            'sm:mt-4',
          ].join(' ')}
          style={{ backgroundColor: screenColor }}
        >
          {covers.map((cover, index) => (
            <ScreenLayer
              key={`screen-${cover.slug}`}
              cover={cover}
              index={index}
              showAtVh={showHideVhs[index] ?? pctToVh(0)}
              hideAtVh={showHideVhs[index + 1] ?? Number.POSITIVE_INFINITY}
              scrollVh={scrollVh}
              reducedMotion={reducedMotion}
            />
          ))}
          {/* "Watch clips" badge — one absolutely-positioned link per
           *  cover, stacked at the bottom-right of the screen. Each fades
           *  in sync with its cover so the visible badge always points to
           *  the active game's clips. */}
          {covers.map((cover, index) => (
            <ClipsBadge
              key={`badge-${cover.slug}`}
              cover={cover}
              index={index}
              showAtVh={showHideVhs[index] ?? pctToVh(0)}
              hideAtVh={showHideVhs[index + 1] ?? Number.POSITIVE_INFINITY}
              scrollVh={scrollVh}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>

        {/* Inset edge highlight along the bottom for depth. */}
        <div
          className="pointer-events-none absolute inset-x-3 bottom-1 h-px rounded-full"
          style={{ backgroundColor: shellEdgeColor }}
        />
      </div>
    </div>
  );
};

// ──────────────────── SHARED CROSS-FADE HOOK ───────────────────

type CoverFadeArgs = {
  scrollVh: MotionValue<number>;
  showAtVh: number;
  hideAtVh: number;
  reducedMotion: boolean;
  /** Index in SCREEN_COVERS — used to pick the single visible cover under
   *  prefers-reduced-motion (first cover only). */
  index: number;
};

/** Returns the opacity MotionValue that drives all layers tied to a given
 *  cover (the cover image, the marquee label, the clips badge). Pulled
 *  into a shared hook so the screen, marquee, and badge stay perfectly
 *  in sync without anyone duplicating the math. */
const useCoverFadeOpacity = ({
  scrollVh,
  showAtVh,
  hideAtVh,
  reducedMotion,
  index,
}: CoverFadeArgs): MotionValue<number> =>
  useTransform(scrollVh, (v) => {
    if (reducedMotion) return index === 0 ? 1 : 0;
    const inN = easeOutCubic(
      mapProgress(v, showAtVh, showAtVh + SCREEN_FADE_VH)
    );
    const outN =
      hideAtVh === Number.POSITIVE_INFINITY
        ? 0
        : easeOutCubic(mapProgress(v, hideAtVh, hideAtVh + SCREEN_FADE_VH));
    return inN * (1 - outN);
  });

// ────────────────────────── SCREEN LAYER ────────────────────────

type ScreenLayerProps = {
  cover: ScreenCover;
  index: number;
  /** Global vh at which this cover should start fading in. */
  showAtVh: number;
  /** Global vh at which this cover should start fading out. Number.POSITIVE_INFINITY
   *  means "last cover, never swap out." */
  hideAtVh: number;
  scrollVh: MotionValue<number>;
  reducedMotion: boolean;
};

/** A single game cover layer inside the CRT. Cross-fades in when its
 *  assigned coin lands, cross-fades out when the next cover's coin lands.
 *  All swap windows are SCREEN_FADE_VH wide so each transition reads as a
 *  real change (not a flicker). */
const ScreenLayer: React.FC<ScreenLayerProps> = ({
  cover,
  index,
  showAtVh,
  hideAtVh,
  scrollVh,
  reducedMotion,
}) => {
  const opacity = useCoverFadeOpacity({
    scrollVh,
    showAtVh,
    hideAtVh,
    reducedMotion,
    index,
  });

  return (
    <motion.img
      src={cover.src}
      // The marquee label and clips badge convey the game name; leave the
      // image alt empty to avoid double-announcement by screen readers.
      alt=""
      className="absolute inset-0 h-full w-full object-cover"
      style={{ opacity }}
      draggable={false}
    />
  );
};

// ────────────────────────── MARQUEE LABEL ───────────────────────

type MarqueeLabelProps = {
  cover: ScreenCover;
  index: number;
  showAtVh: number;
  hideAtVh: number;
  scrollVh: MotionValue<number>;
  reducedMotion: boolean;
};

/** Game-name label that sits on top of the marquee strip. Each label is
 *  absolutely positioned over the strip's invisible sizer so all three
 *  share the same single-line slot and cross-fade in sync with the screen
 *  cover swap. */
const MarqueeLabel: React.FC<MarqueeLabelProps> = ({
  cover,
  index,
  showAtVh,
  hideAtVh,
  scrollVh,
  reducedMotion,
}) => {
  const opacity = useCoverFadeOpacity({
    scrollVh,
    showAtVh,
    hideAtVh,
    reducedMotion,
    index,
  });

  return (
    <motion.span
      className="absolute inset-0 flex items-center justify-center px-3 text-center font-semibold uppercase tracking-[0.18em] text-white"
      style={{ opacity }}
    >
      {cover.name}
    </motion.span>
  );
};

// ─────────────────────────── CLIPS BADGE ────────────────────────

type ClipsBadgeProps = {
  cover: ScreenCover;
  index: number;
  showAtVh: number;
  hideAtVh: number;
  scrollVh: MotionValue<number>;
  reducedMotion: boolean;
};

/** Small "Watch clips" link badge anchored to the bottom-right of the
 *  screen. One per cover, stacked: only the active cover's badge is
 *  visible (and clickable). Inactive badges get pointer-events: none so
 *  invisible badges never intercept clicks. */
const ClipsBadge: React.FC<ClipsBadgeProps> = ({
  cover,
  index,
  showAtVh,
  hideAtVh,
  scrollVh,
  reducedMotion,
}) => {
  const opacity = useCoverFadeOpacity({
    scrollVh,
    showAtVh,
    hideAtVh,
    reducedMotion,
    index,
  });
  const pointerEvents = useTransform(opacity, (o) =>
    o > 0.5 ? 'auto' : 'none'
  );

  return (
    <motion.div
      className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3"
      style={{ opacity, pointerEvents }}
    >
      <Link
        to={`/discover/clips?game=${cover.slug}`}
        className={[
          'inline-flex items-center gap-1 rounded-full',
          'bg-purple-900/85 px-2.5 py-1 text-xs font-semibold text-white',
          'sm:px-3 sm:py-1.5 sm:text-sm',
          'shadow-[0_2px_8px_rgba(0,0,0,0.35)] backdrop-blur-sm',
          'hover:bg-purple-800 focus-visible:outline focus-visible:outline-2',
          'focus-visible:outline-offset-2 focus-visible:outline-white/80',
          'transition-colors',
        ].join(' ')}
        aria-label={`Watch ${cover.name} clips`}
      >
        Watch clips
      </Link>
    </motion.div>
  );
};

// ────────────────────────── FALLING COIN ────────────────────────

type FallingCoinProps = {
  cycle: CoinCycle;
  scrollVh: MotionValue<number>;
  pctToVh: (pct: number) => number;
  viewportH: number;
};

const FallingCoin: React.FC<FallingCoinProps> = ({
  cycle,
  scrollVh,
  pctToVh,
  viewportH,
}) => {
  const fallStartVh = pctToVh(cycle.fallStartPct);
  const landVh = pctToVh(cycle.landPct);
  const hideEndVh = pctToVh(cycle.hideEndPct);

  // How far the coin travels downward, in px. Capped to a sensible minimum
  // so very short viewports still see the coin clearly enter from above.
  const fallDistancePx = Math.max(
    COIN_FALL_DISTANCE_PX_FRAC_OF_VH * viewportH,
    COIN_FALL_DISTANCE_MIN_PX
  );

  // Use ease-in for the fall so the coin accelerates as it drops — feels
  // gravity-correct without needing a physics integrator.
  const easeInCubic = (t: number): number => t * t * t;

  const y = useTransform(scrollVh, (v) => {
    if (v < fallStartVh) return 0;
    const t = Math.min(Math.max(mapProgress(v, fallStartVh, landVh), 0), 1);
    return easeInCubic(t) * fallDistancePx;
  });

  const opacity = useTransform(scrollVh, (v) => {
    // Visible only during the fall + a brief flash at the landing moment.
    if (v < fallStartVh - 0.5) return 0;
    if (v >= hideEndVh) return 0;
    if (v >= landVh) {
      // Fade out quickly after landing — the coin has "gone into" the slot.
      return 1 - easeOutCubic(mapProgress(v, landVh, hideEndVh));
    }
    return easeOutCubic(mapProgress(v, fallStartVh - 0.5, fallStartVh + 1));
  });

  return (
    <motion.div
      className={[
        'pointer-events-none absolute left-1/2 -translate-x-1/2',
        // Start the coin above the visible viewport so its initial position
        // is already offscreen even before the y-offset kicks in.
        '-top-[10vh]',
      ].join(' ')}
      style={{ y, opacity }}
    >
      <img
        src={ASSETS.loonSVG}
        alt=""
        className={[
          'block',
          // Coin size grows up the breakpoint ladder so it remains readable
          // on tall/wide screens, while staying compact on phones.
          'h-14 w-14',
          'sm:h-16 sm:w-16',
          'md:h-20 md:w-20',
          'lg:h-24 lg:w-24',
          'xl:h-28 xl:w-28',
        ].join(' ')}
        draggable={false}
      />
    </motion.div>
  );
};

export default BeatEightArcadePricing;
